import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Download,
  ArrowUpDown,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ReceiptText,
  Filter,
  X,
  ExternalLink,
} from "lucide-react";
import { useAlert } from "../../../context/AlertContext";

/**
 * Sale History UI (React + Tailwind, JSX version)
 * -----------------------------------------------
 * - Reusable, production‑ready, no TypeScript (plain .jsx)
 * - Server‑side pagination + debounced search + filters (date range, status)
 * - Sortable columns, responsive (table → cards on mobile)
 * - World‑class UX: keyboard friendly, accessible, skeletons, empty states
 *
 * Usage:
 * <SaleHistory
 *   fetchSales={yourFetcher}
 *   onRowClick={(sale) => router.push(`/sales/${sale.id}`)}
 * />
 *
 * Expected fetcher:
 *   fetchSales({ page, pageSize, q, dateFrom, dateTo, status, sort })
 *   -> returns { data: Sale[], total: number }
 */

// -----------------------------
// Utilities
// -----------------------------
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatMoney(n) {
  if (Number.isNaN(n)) return "-";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `₹${Number(n || 0).toFixed(2)}`;
  }
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// -----------------------------
// Pagination Component
// -----------------------------
function Paginator({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < pages;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
      <div className="text-sm text-gray-500">
        Page {page} of {pages} • {total.toLocaleString()} results
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows</label>
        <select
          aria-label="Rows per page"
          className="rounded-xl border px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            onClick={() => canPrev && onPageChange(page - 1)}
            disabled={!canPrev}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => canNext && onPageChange(page + 1)}
            disabled={!canNext}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------
// Status Pill
// -----------------------------
function StatusPill({ status }) {
  const map = {
    PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PARTIAL: "bg-amber-100 text-amber-700 border-amber-200",
    DUE: "bg-rose-100 text-rose-700 border-rose-200",
    REFUNDED: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-2xl border px-2.5 py-1 text-xs font-medium",
        map[status]
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />{" "}
      {status}
    </span>
  );
}

// -----------------------------
// Toolbar (Search + Filters)
// -----------------------------
function Toolbar({
  q,
  setQ,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  status,
  setStatus,
  onClear,
  onExport,
}) {
  return (
    <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
      <div className="flex flex-1 items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search retailer, phone, invoice, notes…"
            className="w-full rounded-2xl border bg-white pl-9 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2"
            aria-label="Search sales"
          />
        </div>
        <button
          onClick={onExport}
          className="hidden sm:inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
          aria-label="Export CSV"
        >
          <Download className="h-4 w-4" /> Export
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={dateFrom || ""}
            onChange={(e) => setDateFrom(e.target.value || undefined)}
            className="rounded-2xl border pl-9 pr-3 py-2 shadow-sm"
            aria-label="From date"
          />
        </div>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={dateTo || ""}
            onChange={(e) => setDateTo(e.target.value || undefined)}
            className="rounded-2xl border pl-9 pr-3 py-2 shadow-sm"
            aria-label="To date"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={status || ""}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-2xl border pl-9 pr-8 py-2 shadow-sm"
            aria-label="Status"
          >
            <option value="">All Status</option>
            <option value="PAID">Paid</option>
            <option value="PARTIAL">Partial</option>
            <option value="DUE">Due</option>
            <option value="REFUNDED">Refunded</option>
          </select>
        </div>
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
          aria-label="Clear filters"
        >
          <X className="h-4 w-4" /> Clear
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// Table Header Cell (sortable)
// -----------------------------
function ThSortable({ children, active, dir, onToggle }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
    >
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1 hover:underline"
      >
        {children}
        <ArrowUpDown
          className={cn(
            "h-4 w-4 transition",
            active ? "opacity-100" : "opacity-40"
          )}
        />
        <span className="sr-only">Sort</span>
      </button>
    </th>
  );
}

// -----------------------------
// Row (desktop) and Card (mobile)
// -----------------------------
function SaleRow({ s, onClick }) {
  return (
    <tr className="group hover:bg-gray-50 focus-within:bg-gray-50">
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
        {formatDate(s.date)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-700 flex items-center gap-1">
        <ReceiptText className="h-4 w-4 opacity-60" /> {s.invoice_no}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        <div className="font-medium text-gray-900">
          {s.retailer?.name || "-"}
        </div>
        <div className="text-xs text-gray-500">
          {s.retailer?.mobile_number || ""}{" "}
          {s.retailer?.city ? `• ${s.retailer.city}` : ""}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {s.items_count}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        {formatMoney(s.subtotal)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        {formatMoney(s.discount_amount)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold">
        {formatMoney(s.total)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        {formatMoney(s.paid)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        {formatMoney(s.balance)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        <StatusPill status={s.status} />
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <button
          onClick={() => onClick?.(s)}
          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs shadow-sm hover:bg-gray-50"
        >
          View <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

function SaleCard({ s, onClick }) {
  return (
    <div className="rounded-2xl border p-4 shadow-sm bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-gray-500">{formatDate(s.date)}</div>
          <div className="mt-1 font-semibold text-indigo-700 flex items-center gap-1">
            <ReceiptText className="h-4 w-4" /> {s.invoice_no}
          </div>
          <div className="mt-1 text-sm text-gray-900">
            {s.retailer?.name || "-"}
          </div>
          <div className="text-xs text-gray-500">
            {s.retailer?.mobile_number || ""}{" "}
            {s.retailer?.city ? `• ${s.retailer.city}` : ""}
          </div>
        </div>
        <StatusPill status={s.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="text-gray-500">Items</div>
        <div className="text-right">{s.items_count}</div>
        <div className="text-gray-500">Subtotal</div>
        <div className="text-right">{formatMoney(s.subtotal)}</div>
        <div className="text-gray-500">Discount</div>
        <div className="text-right">{formatMoney(s.discount_amount)}</div>
        <div className="text-gray-500">Paid</div>
        <div className="text-right">{formatMoney(s.paid)}</div>
        <div className="text-gray-500">Balance</div>
        <div className="text-right">{formatMoney(s.balance)}</div>
        <div className="text-gray-900 font-semibold">Total</div>
        <div className="text-right font-semibold">{formatMoney(s.total)}</div>
      </div>
      <div className="mt-3 flex items-center justify-end">
        <button
          onClick={() => onClick?.(s)}
          className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
        >
          View details <ExternalLink className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// Skeletons
// -----------------------------
function RowSkeleton() {
  return (
    <tr>
      {Array.from({ length: 11 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-2xl border p-4 shadow-sm bg-white">
      <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
      <div className="mt-2 h-5 w-1/2 bg-gray-200 rounded animate-pulse" />
      <div className="mt-2 grid grid-cols-2 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-4 w-full bg-gray-200 rounded animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

// -----------------------------
// Empty State
// -----------------------------
function EmptyState({ onClear }) {
  return (
    <div className="rounded-2xl border p-10 bg-white text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
        <Search className="h-6 w-6 text-indigo-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">No sales found</h3>
      <p className="mt-1 text-sm text-gray-500">
        Try adjusting your search or filters.
      </p>
      <div className="mt-4">
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 shadow-sm hover:bg-gray-50"
        >
          <X className="h-4 w-4" /> Clear filters
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// Main Component (JSX)
// -----------------------------
export default function SaleHistoryComponent({ fetchSales, onRowClick }) {
  // query state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ field: "", dir: "asc" });

  const debouncedQ = useDebounced(q, 400);

  // data state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const query = useMemo(
    () => ({
      page,
      pageSize,
      q: debouncedQ || undefined,
      dateFrom,
      dateTo,
      status,
      sort: sort.field ? sort : undefined,
    }),
    [page, pageSize, debouncedQ, dateFrom, dateTo, status, sort]
  );

  async function load() {
    setLoading(true);
    try {
      const res = await fetchSales(query);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  }

  useEffect(() => {
    load(); /* dependencies are expanded below intentionally */
  }, [
    query.page,
    query.pageSize,
    query.q,
    query.dateFrom,
    query.dateTo,
    query.status,
    query.sort && query.sort.field,
    query.sort && query.sort.dir,
  ]);

  function clearFilters() {
    setQ("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setStatus("");
    setSort({ field: "", dir: "asc" });
    setPage(1);
  }

  function toggleSort(field) {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }

  const { showAlert } = useAlert();

  async function exportCsv() {
    try {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([k, v]) => {
        if (!v) return;
        if (typeof v === "object" && k !== "sort") return; // keep simple
        if (k === "sort" && v && v.field) {
          params.set("sortField", String(v.field));
          params.set("sortDir", v.dir);
        } else if (k !== "sort") {
          params.set(k, String(v));
        }
      });
      params.set("export", "csv");
      const resp = await fetch(`/api/sales?${params.toString()}`, {
        headers: { Accept: "text/csv" },
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `sales_${new Date().toISOString()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      // fallback: client‑side CSV for current page
      const header = [
        "Date",
        "Invoice",
        "Retailer",
        "Items",
        "Subtotal",
        "Discount",
        "Total",
        "Paid",
        "Balance",
        "Status",
      ].join(",");
      const lines = rows.map((r) =>
        [
          formatDate(r.date).replace(/,/g, " "),
          r.invoice_no,
          (r.retailer?.name || "").replace(/,/g, " "),
          r.items_count,
          r.subtotal,
          r.discount_amount,
          r.total,
          r.paid,
          r.balance,
          r.status,
        ].join(",")
      );
      const csv = [header, ...lines].join("");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales_page_${page}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed", e);
      showAlert({
        message: "Failed to export CSV. Please try again.",
        type: "error",
      });
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Sale History
            </h1>
            <p className="text-gray-500 mt-1">
              Search, filter, and explore all sales with live pagination.
            </p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
            aria-label="Export CSV"
          >
            <Download className="h-4 w-4" /> Export
          </button>
        </div>

        <div className="mt-4">
          <Toolbar
            q={q}
            setQ={(v) => {
              setQ(v);
              setPage(1);
            }}
            dateFrom={dateFrom}
            setDateFrom={(v) => {
              setDateFrom(v);
              setPage(1);
            }}
            dateTo={dateTo}
            setDateTo={(v) => {
              setDateTo(v);
              setPage(1);
            }}
            status={status}
            setStatus={(v) => {
              setStatus(v);
              setPage(1);
            }}
            onClear={clearFilters}
            onExport={exportCsv}
          />
        </div>

        {/* Desktop Table */}
        <div className="mt-4 hidden md:block">
          <div className="overflow-hidden rounded-2xl border bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <ThSortable
                      active={sort.field === "date"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("date")}
                    >
                      Date
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "invoice_no"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("invoice_no")}
                    >
                      Invoice
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "retailer"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("retailer")}
                    >
                      Retailer
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "items_count"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("items_count")}
                    >
                      Items
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "subtotal"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("subtotal")}
                    >
                      Subtotal
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "discount_amount"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("discount_amount")}
                    >
                      Discount
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "total"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("total")}
                    >
                      Total
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "paid"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("paid")}
                    >
                      Paid
                    </ThSortable>
                    <ThSortable
                      active={sort.field === "balance"}
                      dir={sort.dir}
                      onToggle={() => toggleSort("balance")}
                    >
                      Balance
                    </ThSortable>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading &&
                    firstLoad &&
                    Array.from({ length: 8 }).map((_, i) => (
                      <RowSkeleton key={i} />
                    ))}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={11} className="p-8">
                        <EmptyState onClear={clearFilters} />
                      </td>
                    </tr>
                  )}
                  {!firstLoad && loading && rows.length > 0 && (
                    <tr>
                      <td
                        colSpan={11}
                        className="p-6 text-center text-sm text-gray-500"
                      >
                        <Loader2 className="inline-block h-4 w-4 animate-spin mr-2" />{" "}
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    rows.map((s) => (
                      <SaleRow key={s.id} s={s} onClick={onRowClick} />
                    ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 pb-3">
              <Paginator
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="mt-4 grid md:hidden grid-cols-1 gap-3">
          {loading &&
            firstLoad &&
            Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
          {!loading && rows.length === 0 && (
            <EmptyState onClear={clearFilters} />
          )}
          {!loading &&
            rows.map((s) => <SaleCard key={s.id} s={s} onClick={onRowClick} />)}
          {rows.length > 0 && (
            <Paginator
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}
